'use client'

import Header from '@/components/header'
import {
  ArrowLeft,
  Send,
  RefreshCw,
  Sparkles,
  Download,
  Copy,
  Check,
  Play,
  Pause,
} from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useState, useEffect, useRef } from 'react'
import Api from '@/api/h5Api'
import Player from 'xgplayer'
import 'xgplayer/dist/index.min.css'

interface VideoItem {
  uid: string
  createTime: number
  info: {
    message?: string
    originalMessage?: string
    cdnUrl?: string
    videoUri?: string
    status?: string
  }
}

interface ApiResponse<T = any> {
  code: number
  message?: string
  data: T
}

export default function Page() {
  const router = useRouter()
  const [dataSource, setDataSource] = useState<VideoItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [inputMessage, setInputMessage] = useState('')
  const [current, setCurrent] = useState(1)
  const [isHasMore, setIsHasMore] = useState(true)
  const [copiedText, setCopiedText] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const playersRef = useRef<Map<string, Player>>(new Map())

  // 获取提示词
  const getPrompt = (item: VideoItem) => {
    if (item.info?.originalMessage) {
      return item.info.originalMessage
    } else if (item.info?.message) {
      return item.info.message
    }
    return ''
  }

  // 滚动到顶部
  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  // 搜索视频列表
  const handleSearch = async (pageNum = 1) => {
    if (isLoading) return

    setIsLoading(true)
    try {
      const res = (await Api.aiVideoAppSearch({
        pageNum,
        pageSize: 10,
      })) as unknown as ApiResponse<{
        list: VideoItem[]
        total: number
      }>

      if (res.code === 200) {
        const newList = res.data?.list || []
        if (pageNum === 1) {
          setDataSource(newList)
          scrollToTop()
        } else {
          setDataSource((prev) => [...prev, ...newList])
        }
        setIsHasMore(newList.length === 10)
        setCurrent(pageNum)
      }
    } catch (error) {
      console.error('获取视频列表失败:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // 初始加载
  useEffect(() => {
    handleSearch(1)
  }, [])

  // 提交生成视频
  const handleSubmit = async () => {
    if (!inputMessage.trim() || isGenerating) return

    const message = inputMessage.trim()
    setIsGenerating(true)
    setInputMessage('')

    try {
      const res = (await Api.aiVideoAdd({
        info: {
          message,
          type: 'm2v_txt2video',
        },
      })) as unknown as ApiResponse<{
        uid: string
        cdnUrl: string
      }>

      if (res.code === 200) {
        // 刷新列表
        await handleSearch(1)
      } else {
        alert(res.message || '生成失败，请稍后重试')
      }
    } catch (error: any) {
      console.error('生成视频失败:', error)
      alert(error?.message || '生成失败，请稍后重试')
    } finally {
      setIsGenerating(false)
    }
  }

  // 加载更多
  const handleLoadMore = () => {
    if (!isLoading && isHasMore) {
      handleSearch(current + 1)
    }
  }

  // 复制文本
  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedText(text)
      setTimeout(() => setCopiedText(null), 2000)
    } catch (error) {
      console.error('复制失败:', error)
    }
  }

  // 下载视频
  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // 初始化视频播放器
  const initPlayer = (uid: string, videoUrl: string) => {
    // 清理旧的播放器实例
    const existingPlayer = playersRef.current.get(uid)
    if (existingPlayer) {
      existingPlayer.destroy()
      playersRef.current.delete(uid)
    }

    // 创建新播放器
    try {
      const player = new Player({
        id: `video-${uid}`,
        url: videoUrl,
        poster: '',
        fluid: true,
        autoplay: false,
        lang: 'zh-cn',
      })
      playersRef.current.set(uid, player)
    } catch (error) {
      console.error('初始化播放器失败:', error)
    }
  }

  // 清理播放器
  useEffect(() => {
    return () => {
      playersRef.current.forEach((player) => {
        player.destroy()
      })
      playersRef.current.clear()
    }
  }, [])

  // 监听数据变化，初始化播放器
  useEffect(() => {
    dataSource.forEach((item) => {
      if (item.info?.cdnUrl) {
        // 延迟初始化，确保 DOM 已渲染
        setTimeout(() => {
          const element = document.getElementById(`video-${item.uid}`)
          if (element && !playersRef.current.has(item.uid)) {
            initPlayer(item.uid, item.info.cdnUrl!)
          }
        }, 100)
      }
    })
  }, [dataSource])

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Header />

      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* 顶部标题 */}
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => router.back()}
            className="p-2 rounded-lg hover:bg-white/50 backdrop-blur-sm transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              AI 视频生成
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              使用 Google Veo 生成高质量视频
            </p>
          </div>
        </div>

        {/* 创作输入区 */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/20">
          <div className="flex items-start gap-3">
            <Sparkles className="w-6 h-6 text-indigo-500 mt-3 flex-shrink-0" />
            <div className="flex-1">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSubmit()
                  }
                }}
                placeholder="描述你想要生成的视频内容... (例如：一只可爱的小猫在草地上奔跑)"
                className="w-full min-h-[120px] p-4 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none resize-none transition-all"
                maxLength={2000}
                disabled={isGenerating}
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-sm text-gray-400">
                  {inputMessage.length}/2000
                </span>
                <button
                  onClick={handleSubmit}
                  disabled={!inputMessage.trim() || isGenerating}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-medium hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all flex items-center gap-2"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      生成中...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      生成视频
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 历史视频列表 */}
        <div
          ref={scrollContainerRef}
          className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/20"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-800">历史视频</h2>
            <button
              onClick={() => handleSearch(1)}
              disabled={isLoading}
              className="p-2 rounded-lg hover:bg-white/50 transition-all"
            >
              <RefreshCw
                className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`}
              />
            </button>
          </div>

          {/* 视频网格 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dataSource.map((item) => (
              <div
                key={item.uid}
                className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden border border-gray-100"
              >
                {/* 视频播放器 */}
                <div className="relative bg-gray-900 aspect-video">
                  {item.info?.cdnUrl ? (
                    <div
                      id={`video-${item.uid}`}
                      className="w-full h-full"
                    ></div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center text-white">
                        <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
                        <p className="text-sm">生成中...</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* 视频信息 */}
                <div className="p-4">
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                    {getPrompt(item)}
                  </p>

                  <div className="flex items-center gap-2">
                    {item.info?.cdnUrl && (
                      <>
                        <button
                          onClick={() =>
                            handleCopy(item.info.cdnUrl || '')
                          }
                          className="flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm flex items-center justify-center gap-2 transition-all"
                        >
                          {copiedText === item.info.cdnUrl ? (
                            <>
                              <Check className="w-4 h-4 text-green-500" />
                              已复制
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              复制
                            </>
                          )}
                        </button>
                        <button
                          onClick={() =>
                            handleDownload(
                              item.info.cdnUrl!,
                              `video-${item.uid}.mp4`
                            )
                          }
                          className="flex-1 px-3 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-sm flex items-center justify-center gap-2 transition-all"
                        >
                          <Download className="w-4 h-4" />
                          下载
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 加载更多 */}
          {dataSource.length > 0 && isHasMore && (
            <div className="mt-6 text-center">
              <button
                onClick={handleLoadMore}
                disabled={isLoading}
                className="px-6 py-3 bg-white hover:bg-gray-50 rounded-xl text-gray-600 font-medium border border-gray-200 hover:shadow-md disabled:opacity-50 transition-all"
              >
                {isLoading ? '加载中...' : '加载更多'}
              </button>
            </div>
          )}

          {/* 空状态 */}
          {dataSource.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <Play className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-400">还没有生成过视频</p>
              <p className="text-gray-400 text-sm mt-2">
                在上方输入描述，开始创作吧！
              </p>
            </div>
          )}
        </div>
      </div>

      <div ref={messagesEndRef} />
    </div>
  )
}
